import { isHttpUrl } from './entities'
import type { ApiChatServerAttachmentSettings } from './rest.types'
import type { ChatServerAttachment, ChatServerAttachmentKind, ChatServerAttachmentSize } from './types'

/**
 * Filter a raw `message.attachments` value down to renderable attachments:
 * items without a resolvable media URL are dropped, unknown `kind` degrades to
 * `'document'`. Returns attachments ordered by `position`.
 */
export const sanitizeAttachments = (raw: unknown): ChatServerAttachment[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .flatMap((item): ChatServerAttachment[] => {
      if (typeof item !== 'object' || item === null) return []
      const attachment = item as ChatServerAttachment
      if (!attachmentUrl(attachment)) return []
      return [ATTACHMENT_KINDS.includes(attachment.kind) ? attachment : { ...attachment, kind: 'document' }]
    })
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
}

/** The attachment's primary media URL: `url` for documents, the best `sizes` entry otherwise. */
export const attachmentUrl = (attachment: ChatServerAttachment, targetWidth?: number): string | null => {
  if (isMediaUrl(attachment.url)) return attachment.url
  const size = pickAttachmentSize(attachment.sizes, targetWidth)
  return size ? size.url : null
}

/** Smallest size covering `targetWidth`, else the largest available (original when widths are null). */
export const pickAttachmentSize = (sizes: ChatServerAttachmentSize[] | undefined, targetWidth?: number): ChatServerAttachmentSize | null => {
  const valid = (sizes ?? []).filter(s => isMediaUrl(s?.url))
  if (!valid.length) return null
  if (targetWidth) {
    const covering = valid.filter(s => (s.width ?? Infinity) >= targetWidth)
    if (covering.length) return covering.reduce((a, b) => ((a.width ?? Infinity) <= (b.width ?? Infinity) ? a : b))
  }
  return valid.reduce((a, b) => ((a.width ?? Infinity) >= (b.width ?? Infinity) ? a : b))
}

/** A relative `/media/...` path, an absolute http(s) URL, or a local `blob:`/`file:` preview (optimistic sends). */
export const isMediaUrl = (url: unknown): url is string =>
  (typeof url === 'string' && (url.startsWith('/') || url.startsWith('blob:') || url.startsWith('file:'))) || isHttpUrl(url)

/** Upload kind for a picked file: whitelisted image mimes go as `photo`, everything else as `document`. */
export const attachmentKindForFile = (mimeType: string, settings: ApiChatServerAttachmentSettings): 'photo' | 'document' =>
  settings.photo_mime_types.includes(mimeType) ? 'photo' : 'document'

/** Client-side pre-check mirroring the server's presign validation. */
export const validateAttachmentFile = (byteSize: number, kind: 'photo' | 'document', settings: ApiChatServerAttachmentSettings): 'too_large' | null =>
  byteSize > (kind === 'photo' ? settings.photo_max_bytes : settings.document_max_bytes) ? 'too_large' : null

const ATTACHMENT_KINDS: readonly ChatServerAttachmentKind[] = ['photo', 'document', 'video', 'audio', 'voice', 'sticker']
